import { useEffect, type RefObject } from 'react'

/**
 * Stops wheel scrolling inside a floating panel from "chaining" to the host page.
 *
 * The sticky chat widget is layered over the real-estate host page. Without this,
 * scrolling the conversation's message list past its top/bottom (or scrolling over
 * a non-scrollable region like the window header/composer) lets the wheel event
 * reach the document and scroll the page underneath.
 *
 * React's synthetic onWheel is passive and can't call preventDefault, so we attach
 * a native non-passive listener on the panel root and:
 * - let an inner scrollable list scroll natively, blocking only at its boundaries;
 * - block host-page scroll entirely when the cursor is over a non-scrollable region.
 *
 * Mirrors the guard already used by the recent-chats sidebar (see
 * `components/messenger/left-sidebar-chat/index.tsx`).
 */
const useWheelScrollTrap = <T extends HTMLElement>(rootRef: RefObject<T | null>, enabled = true) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root || !enabled) return

    const isScrollable = (el: HTMLElement) => {
      const overflowY = getComputedStyle(el).overflowY
      return (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight
    }

    const handleWheel = (e: WheelEvent) => {
      let el = e.target as HTMLElement | null
      while (el) {
        if (isScrollable(el)) {
          const atTop = el.scrollTop <= 0 && e.deltaY < 0
          const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight && e.deltaY > 0
          // Let the list scroll natively; only block chaining at its boundaries.
          if (atTop || atBottom) e.preventDefault()
          return
        }
        if (el === root) break
        el = el.parentElement
      }
      // Cursor is over a non-scrollable region → block host-page scroll.
      e.preventDefault()
    }

    root.addEventListener('wheel', handleWheel, { passive: false })
    return () => root.removeEventListener('wheel', handleWheel)
  }, [rootRef, enabled])
}

export default useWheelScrollTrap
